import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaUpload, FaCamera, FaCheck, FaExclamationCircle, FaLock } from "react-icons/fa";
import { apiService } from "../../../components/Config/api.service";
import EnableReopenClosedMonthModal from "./EnableReopenClosedMonthModal";
import AmountInput from "../../../components/Utils/AmountInput";
import { formatMoney } from "../../../components/Utils/FormatUtils";

const Company = ({ isSuperadmin = false }) => {
    const { t } = useTranslation();
    const [company, setCompany] = useState({
        id: "",
        name: "",
        address: "",
        other_name: "",
        nb_day_week: "",
        nb_hour_day: "",
        total_leave_days_per_month: "",
        cfce: true,
        display_heure_sup: false,
        work_accident: 1.0,
        apply_external_taxes: true,
        apply_external_work_accident: false,
        leave_management_mode: "paid_leave_no_ic",
        absence_count_weekends: true,
        absence_count_public_holidays: true,
        month_lock_enabled: true,
        show_reset_pending_on_closed_month: false,
        has_daily_salary_input: false,
        allow_overtime_date_outside_month: false,
        transport_premium: 0,
        transport_premium_override: false,
        transport_premium_is_subsidiary: false,
        transport_premium_effective: 0,
        transport_premium_source_name: "",
        logo: false,
    });
    const [isEnableReopenModalOpen, setIsEnableReopenModalOpen] = useState(false);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyMsg, setCompanyMsg] = useState({ type: "", text: "" });
    const fileInputRef = useRef();

    useEffect(() => {
        setCompanyLoading(true);
        apiService.getElement("/company/info")
            .then((res) => {
                setCompany(res.data);
                try {
                    localStorage.setItem('companyInfo', JSON.stringify(res.data));
                } catch (e) {
                    // ignore storage errors
                }
            })
            .catch(() => setCompanyMsg({ type: "error", text: t('companySettings.loadError') }))
            .finally(() => setCompanyLoading(false));
    }, []);

    const handleCompanyChange = (e) => {
        setCompany({ ...company, [e.target.name]: e.target.value });
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        setCompanyLoading(true);
        setCompanyMsg({ type: "", text: "" });
        try {
            await apiService.saveElement("/company/upload_logo", formData, true);
            const res = await apiService.getElement("/company/info");
            setCompany(res.data);
            try {
                localStorage.setItem('companyInfo', JSON.stringify(res.data));
            } catch (e) {}
            // notify other windows/components
            try { window.dispatchEvent(new Event('companyChanged')); } catch (e) {}
            setCompanyMsg({ type: "success", text: t('companySettings.logoUpdated') });
        } catch {
            setCompanyMsg({ type: "error", text: t('companySettings.logoChangeError') });
        }
        setCompanyLoading(false);
    };

    // Filiale sans surcharge : le montant hérité s'affiche en lecture seule.
    const transportPremiumInherited = company.transport_premium_is_subsidiary && !company.transport_premium_override;

    const handleTransportOverrideChange = (e) => {
        const override = e.target.checked;
        // En cochant, on part du montant hérité plutôt que d'une valeur oubliée.
        setCompany({
            ...company,
            transport_premium_override: override,
            transport_premium: override ? company.transport_premium_effective : company.transport_premium,
        });
    };

    const handleCompanySave = async (e) => {
        e.preventDefault();
        setCompanyMsg({ type: "", text: "" });
        if (!transportPremiumInherited) {
            const amount = company.transport_premium;
            if (amount === "" || amount === null || amount === undefined || Number(amount) < 0) {
                setCompanyMsg({ type: "error", text: t('companySettings.transportPremiumInvalid') });
                return;
            }
        }
        setCompanyLoading(true);
        try {
            // Option réservée au SuperAdmin (Support#40) : ne pas l'envoyer sinon.
            const { show_reset_pending_on_closed_month, ...payload } = company;
            await apiService.saveElement(
                "/company/update",
                isSuperadmin ? company : payload
            );
            // Recharger : la valeur applicable et sa source dépendent de la surcharge.
            const res = await apiService.getElement("/company/info");
            setCompany(res.data);
            setCompanyMsg({ type: "success", text: t('companySettings.settingsSaved') });
        } catch {
            setCompanyMsg({ type: "error", text: t('companySettings.updateError') });
        }
        setCompanyLoading(false);
    };

    return (
        <form onSubmit={handleCompanySave} className="space-y-8">
            {/* Section Logo et Informations générales */}
            <div className="space-y-6">
                {/* <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 rounded-full p-2">
                        <FaBuilding className="text-blue-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Informations générales</h4>
                </div> */}

                {/* Logo Section */}
                <div className="flex flex-col md:flex-row items-start gap-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex flex-col items-center gap-4">
                        {company.logo ? (
                            <img
                                src={`data:image/png;base64,${company.logo}`}
                                alt="Logo"
                                className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
                            />
                        ) : (
                            <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-dashed border-gray-300">
                                <FaCamera className="text-gray-400" size={24} />
                            </div>
                        )}
                        <button
                            type="button"
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm"
                            onClick={() => fileInputRef.current.click()}
                            disabled={companyLoading}
                        >
                            <FaUpload size={14} />
                            {company.logo ? t('companySettings.changeLogo') : t('companySettings.addLogo')}
                        </button>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleLogoChange}
                        />
                    </div>

                    <div className="flex-1 space-y-4">
                        <div>
                            <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('settingsPage.companyName')} *
                            </label>
                            <input
                                id="company-name"
                                name="name"
                                value={company.name}
                                onChange={handleCompanyChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder={t('companySettings.companyNamePlaceholder')}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="company-address" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('settingsPage.address')}
                            </label>
                            <textarea
                                id="company-address"
                                name="address"
                                value={company.address || ""}
                                onChange={handleCompanyChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder={t('companySettings.addressPlaceholder')}
                                rows={3}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Paramètres de travail */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-100 rounded-full p-2">
                        <FaBuilding className="text-green-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">{t('companySettings.workSettings')}</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('companySettings.workDaysPerWeek')}
                        </label>
                        <input
                            name="nb_day_week"
                            value={company.nb_day_week || ""}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="1"
                            max="7"
                            placeholder={t('companySettings.exampleFive')}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('companySettings.workHoursPerDay')}
                        </label>
                        <input
                            name="nb_hour_day"
                            value={company.nb_hour_day || ""}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="1"
                            max="24"
                            placeholder={t('companySettings.exampleEight')}
                        />
                    </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                        name="month_lock_enabled"
                        type="checkbox"
                        checked={Boolean(company.month_lock_enabled)}
                        onChange={(e) => setCompany({ ...company, month_lock_enabled: e.target.checked })}
                        className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                        id="month_lock_enabled"
                    />
                    <label htmlFor="month_lock_enabled" className="text-sm font-medium text-gray-700">
                        {t('companySettings.monthLockLabel')}
                        <span className="block text-xs font-normal text-gray-500 mt-1">
                            {t('companySettings.monthLockHint')}
                        </span>
                    </label>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                        name="has_daily_salary_input"
                        type="checkbox"
                        checked={Boolean(company.has_daily_salary_input)}
                        onChange={(e) => setCompany({ ...company, has_daily_salary_input: e.target.checked })}
                        className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                        id="has_daily_salary_input"
                    />
                    <label htmlFor="has_daily_salary_input" className="text-sm font-medium text-gray-700">
                        {t('companySettings.dailySalaryInputLabel')}
                        <span className="block text-xs font-normal text-gray-500 mt-1">
                            {t('companySettings.dailySalaryInputHint')}
                        </span>
                    </label>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                        name="allow_overtime_date_outside_month"
                        type="checkbox"
                        checked={Boolean(company.allow_overtime_date_outside_month)}
                        onChange={(e) => setCompany({ ...company, allow_overtime_date_outside_month: e.target.checked })}
                        className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                        id="allow_overtime_date_outside_month"
                    />
                    <label htmlFor="allow_overtime_date_outside_month" className="text-sm font-medium text-gray-700">
                        {t('companySettings.allowOvertimeDateOutsideMonthLabel')}
                        <span className="block text-xs font-normal text-gray-500 mt-1">
                            {t('companySettings.allowOvertimeDateOutsideMonthHint')}
                        </span>
                    </label>
                </div>
            </div>

            {/* Section Gestion des congés */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-100 rounded-full p-2">
                        <FaBuilding className="text-purple-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">{t('companySettings.leaveManagement')}</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('companySettings.leaveManagementMode')}
                        </label>
                        <select
                            name="leave_management_mode"
                            value={company.leave_management_mode || "paid_leave_no_ic"}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                        >
                            <option value="paid_leave_no_ic">
                                {t('companySettings.paidLeaveNoIc')}
                            </option>
                            <option value="unpaid_leave_with_ic">
                                {t('companySettings.unpaidLeaveWithIc')}
                            </option>
                            <option value="paid_leave_with_ic">
                                {t('companySettings.paidLeaveWithIc')}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('companySettings.leaveDaysPerMonth')}
                        </label>
                        <input
                            name="total_leave_days_per_month"
                            value={company.total_leave_days_per_month || ""}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder={t('companySettings.exampleTwoPointFive')}
                        />
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <input
                            name="absence_count_weekends"
                            type="checkbox"
                            checked={Boolean(company.absence_count_weekends)}
                            onChange={(e) => setCompany({ ...company, absence_count_weekends: e.target.checked })}
                            className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                            id="absence_count_weekends"
                        />
                        <label htmlFor="absence_count_weekends" className="text-sm font-medium text-gray-700">
                            {t('companySettings.absenceCountWeekends')}
                            <span className="block text-xs font-normal text-gray-500 mt-1">
                                {t('companySettings.absenceCountWeekendsHint')}
                            </span>
                        </label>
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <input
                            name="absence_count_public_holidays"
                            type="checkbox"
                            checked={Boolean(company.absence_count_public_holidays)}
                            onChange={(e) => setCompany({ ...company, absence_count_public_holidays: e.target.checked })}
                            className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                            id="absence_count_public_holidays"
                        />
                        <label htmlFor="absence_count_public_holidays" className="text-sm font-medium text-gray-700">
                            {t('companySettings.absenceCountPublicHolidays')}
                            <span className="block text-xs font-normal text-gray-500 mt-1">
                                {t('companySettings.absenceCountPublicHolidaysHint')}
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Section Cotisations */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-orange-100 rounded-full p-2">
                        <FaBuilding className="text-orange-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">{t('companySettings.contributionsAndCharges')}</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('companySettings.workAccidentRate')}
                        </label>
                        <input
                            name="work_accident"
                            value={company.work_accident}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="default: 1.0"
                           
                        />
                    </div>
                    <div className="flex items-center h-full">
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                            <input
                                name="cfce"
                                type="checkbox"
                                checked={company.cfce}
                                onChange={(e) => setCompany({ ...company, cfce: e.target.checked })}
                                className="w-4 h-4 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                                id="cfce"
                            />
                            <label htmlFor="cfce" className="text-sm font-medium text-gray-700">
                                {t('companySettings.cfceLabel')}
                            </label>
                        </div>
                    </div>
                    <div className="flex items-center h-full">
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                            <input
                                name="display_heure_sup"
                                type="checkbox"
                                checked={Boolean(company.display_heure_sup)}
                                onChange={(e) => setCompany({ ...company, display_heure_sup: e.target.checked })}
                                className="w-4 h-4 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                                id="display_heure_sup"
                            />
                            <label htmlFor="display_heure_sup" className="text-sm font-medium text-gray-700">
                                {t('companySettings.displayOvertimeLabel')}
                            </label>
                        </div>
                    </div>
                    <div className="flex items-center h-full">
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                            <input
                                name="apply_external_taxes"
                                type="checkbox"
                                checked={Boolean(company.apply_external_taxes ?? true)}
                                onChange={(e) => setCompany({ ...company, apply_external_taxes: e.target.checked })}
                                className="w-4 h-4 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                                id="apply_external_taxes"
                            />
                            <label htmlFor="apply_external_taxes" className="text-sm font-medium text-gray-700">
                                {t('companySettings.applyExternalTaxesLabel', 'Appliquer les taxes pour les prestataires et stagiaires')}
                            </label>
                        </div>
                    </div>
                    <div className="flex items-center h-full">
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                            <input
                                name="apply_external_work_accident"
                                type="checkbox"
                                checked={Boolean(company.apply_external_work_accident ?? false)}
                                onChange={(e) => setCompany({ ...company, apply_external_work_accident: e.target.checked })}
                                className="w-4 h-4 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                                id="apply_external_work_accident"
                            />
                            <label htmlFor="apply_external_work_accident" className="text-sm font-medium text-gray-700">
                                {t('companySettings.applyExternalWorkAccidentLabel', "Appliquer l'Accident du Travail (AT) pour les stagiaires (Plafond : 63 000 FCFA)")}
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Sécurité renforcée : actions sensibles */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-red-100 rounded-full p-2">
                        <FaLock className="text-red-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">{t('companySettings.enhancedSecurity')}</h4>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                        name="show_reset_pending_on_closed_month"
                        type="checkbox"
                        checked={Boolean(company.show_reset_pending_on_closed_month)}
                        disabled={!isSuperadmin}
                        // Activer = action hyper sensible : passage obligé par la modale. Désactiver est immédiat.
                        onChange={(e) => e.target.checked
                            ? setIsEnableReopenModalOpen(true)
                            : setCompany({ ...company, show_reset_pending_on_closed_month: false })}
                        className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                        id="show_reset_pending_on_closed_month"
                    />
                    <label htmlFor="show_reset_pending_on_closed_month" className="text-sm font-medium text-gray-700">
                        {t('companySettings.showResetPendingLabel')}
                        <span className="block text-xs font-normal text-gray-500 mt-1">
                            {t('companySettings.showResetPendingHint')}
                            {!isSuperadmin && ` ${t('companySettings.showResetPendingSuperadminOnly')}`}
                        </span>
                    </label>
                </div>
            </div>

            <EnableReopenClosedMonthModal
                isOpen={isEnableReopenModalOpen}
                onClose={() => setIsEnableReopenModalOpen(false)}
                onConfirm={() => {
                    setCompany({ ...company, show_reset_pending_on_closed_month: true });
                    setIsEnableReopenModalOpen(false);
                }}
            />

            {/* Section Prime de transport (Yros/Support#39) */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 rounded-full p-2">
                        <FaBuilding className="text-blue-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">{t('companySettings.transportPremiumSection')}</h4>
                </div>

                {company.transport_premium_is_subsidiary && (
                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <input
                            name="transport_premium_override"
                            type="checkbox"
                            checked={Boolean(company.transport_premium_override)}
                            onChange={handleTransportOverrideChange}
                            className="w-4 h-4 mt-0.5 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                            id="transport_premium_override"
                        />
                        <label htmlFor="transport_premium_override" className="text-sm font-medium text-gray-700">
                            {t('companySettings.transportPremiumOverrideLabel')}
                            <span className="block text-xs font-normal text-gray-500 mt-1">
                                {t('companySettings.transportPremiumOverrideHint')}
                            </span>
                        </label>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="transport_premium" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('companySettings.transportPremiumLabel')}
                        </label>
                        {transportPremiumInherited ? (
                            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                                <span className="font-medium">{formatMoney(company.transport_premium_effective)}</span>
                                <span className="block text-xs text-gray-500 mt-1">
                                    {t('companySettings.transportPremiumInheritedFrom', { company: company.transport_premium_source_name })}
                                </span>
                            </div>
                        ) : (
                            <AmountInput
                                id="transport_premium"
                                name="transport_premium"
                                value={company.transport_premium}
                                onChange={(value) => setCompany({ ...company, transport_premium: value })}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            />
                        )}
                        <p className="text-xs text-gray-500 mt-1">{t('companySettings.transportPremiumHint')}</p>
                    </div>
                </div>
            </div>

            {/* Message de statut */}
            {companyMsg.text && (
                <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                    companyMsg.type === "success" 
                        ? "bg-green-50 border-green-200 text-green-800" 
                        : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    {companyMsg.type === "success" ? (
                        <FaCheck className="text-green-600" size={16} />
                    ) : (
                        <FaExclamationCircle className="text-red-600" size={16} />
                    )}
                    <span className="font-medium">{companyMsg.text}</span>
                </div>
            )}

            {/* Bouton de sauvegarde */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    disabled={companyLoading}
                >
                    {companyLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            {t('companySettings.saving')}
                        </>
                    ) : (
                        <>
                            <FaCheck size={16} />
                            {t('recapitulatif.saveChanges')}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default Company;
